import { Api, BackofficeLoginRequest, BackofficeLoginResponse, ContactRequest } from '../api/BackofficeApi';

// Production API URL - change this when deploying to production
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://backoffice-api.enerjet.co.il'
  : 'http://localhost:9091';

// Create API instance with auth interceptor
const api = new Api({
  baseUrl: API_BASE_URL,
});

// Add auth token to requests and handle auth errors
const originalRequest = api.request.bind(api);
api.request = async (config: any) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: token,
    };
  }
  
  try {
    return await originalRequest(config);
  } catch (error: any) {
    // Handle authentication errors (401) - redirect to login
    if (error.status === 401 || 
        (error.message && error.message.includes('authorization mismatch'))) {
      // Clear auth data and redirect to login
      localStorage.clear();
      window.location.href = '/login';
    }
    // For 403 errors, don't redirect - just throw the error
    // 403 means user is authenticated but lacks permission
    throw error;
  }
};

export const authAPI = {
  login: async (credentials: BackofficeLoginRequest): Promise<BackofficeLoginResponse> => {
    const response = await api.login.login(credentials);
    return response.data;
  },
  
  getOrganization: async (organizationName: string) => {
    const response = await api.organizations.getOrganization({ organizationName });
    return response.data;
  },
  
  getUsers: async (organizationName?: string) => {
    const params = organizationName ? { organizationName } : {};
    const response = await api.organizations.listUsers(params as any);
    return response.data;
  },

  getUser: async (organizationName: string, username: string) => {
    const response = await api.organizations.getUser({ organizationName, username });
    return response.data;
  },
  
  getActivities: async (params?: { from?: number; to?: number; limit?: number; offset?: number; organization?: string; username?: string }) => {
    const response = await api.activities.listActivities(params);
    return response.data;
  },

  getAllActivities: async (params?: { from?: number; to?: number; organization?: string; username?: string }) => {
    const allActivities: any[] = [];
    let offset = 0;
    const limit = 1000; // Maximum limit per request
    let hasMore = true;

    while (hasMore) {
      const response = await api.activities.listActivities({
        ...params,
        limit,
        offset
      });
      
      const activities = response.data;
      allActivities.push(...activities);
      
      // If we got fewer items than the limit, we've reached the end
      hasMore = activities.length === limit;
      offset += limit;
    }

    return allActivities;
  },

  // New efficient analytics endpoints
  getActivityCounts: async () => {
    const response = await api.analytics.getActivityCounts();
    return response.data;
  },

  getUserActivitySummary: async () => {
    const response = await api.analytics.getUserActivitySummary();
    return response.data;
  },
  
  getExceptions: async (params?: { from?: number; to?: number; limit?: number }) => {
    const response = await api.exceptions.listExceptions(params);
    return response.data;
  },

  // Analytics endpoints
  getAnalyticsSummary: async () => {
    const response = await api.analytics.getAnalyticsSummary();
    return response.data;
  },

  getAnalyticsTrends: async (params?: { from?: number; to?: number }) => {
    const response = await api.analytics.getAnalyticsTrends(params);
    return response.data;
  },

  // Admin endpoints
  getOrganizations: async () => {
    const response = await api.organizations.listOrganizations();
    return response.data;
  },

  createOrganization: async (organization: any) => {
    const response = await api.organizations.createOrganization(organization);
    return response.data;
  },

  updateOrganization: async (organizationName: string, organization: any) => {
    const response = await api.organizations.updateOrganization({ organizationName }, organization);
    return response.data;
  },

  deleteOrganization: async (organizationName: string) => {
    await api.organizations.deleteOrganization({ organizationName });
  },

  // Permission groups
  getPermissionGroups: async () => {
    const response = await api.permissionGroups.listPermissionGroups();
    return response.data;
  },

  createPermissionGroup: async (permissionGroup: any) => {
    const response = await api.permissionGroups.createPermissionGroup(permissionGroup);
    return response.data;
  },

  updatePermissionGroup: async (name: string, permissionGroup: any) => {
    const response = await api.permissionGroups.updatePermissionGroup({ name }, permissionGroup);
    return response.data;
  },

  deletePermissionGroup: async (name: string) => {
    await api.permissionGroups.deletePermissionGroup({ name });
  },

  updateOrganizationPermissionGroups: async (organizationName: string, permissionGroups: any[]) => {
    const response = await api.organizations.updateOrganizationPermissionGroups({ organizationName }, permissionGroups);
    return response.data;
  },

  // User management
  createUser: async (organizationName: string, user: any) => {
    const response = await api.organizations.createUser({ organizationName }, user);
    return response.data;
  },

  updateUser: async (organizationName: string, username: string, user: any) => {
    const response = await api.organizations.updateUser({ organizationName, username }, user);
    return response.data;
  },

  deleteUser: async (organizationName: string, username: string) => {
    await api.organizations.deleteUser({ organizationName, username });
  },

  updateUserCredentials: async (organizationName: string, username: string, credentials: { windowsCredentials: string }) => {
    const response = await api.organizations.updateUserCredentials({ organizationName, username }, credentials);
    return response.data;
  },

  // Properties management
  getProperties: async () => {
    const response = await api.properties.getProperties();
    return response.data;
  },

  updateProperties: async (properties: { minVersion: string; maxVersion: string }) => {
    const response = await api.properties.updateProperties(properties);
    return response.data;
  },

  // Project analytics
  getProjectAnalytics: async (startDate?: number, endDate?: number) => {
    const response = await api.analytics.getProjectAnalytics({ startDate, endDate });
    return response.data;
  },

  // Project management
  getProjects: async () => {
    const response = await api.projects.listProjects();
    return response.data;
  },

  getProject: async (projectId: string) => {
    const response = await api.projects.getProject({ projectId });
    return response.data;
  },

  // Contact form (public, no auth required)
  submitContact: async (data: ContactRequest) => {
    await api.contact.submitContact(data);
  }
};

export default api;
